import { useDealership } from '@/contexts/DealershipContext'
import { createDealership } from '@/services/dealership.api'
import { DealershipGroup } from '@/types'
import { Box, Button, Card, Divider, Group, Paper, Radio, Select, Stack, Text, TextInput, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconArrowLeft, IconBuilding, IconCheck, IconCopy, IconMail, IconMapPin, IconPhone } from '@tabler/icons-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * Format phone number to (XXX) XXX-XXXX format
 */
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '')

  // Limit to 10 digits
  const limitedDigits = digits.slice(0, 10)

  // Format based on length
  if (limitedDigits.length === 0) return ''
  if (limitedDigits.length <= 3) return `(${limitedDigits}`
  if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`
}

interface AddDealershipFormValues {
  // Basic Info
  creationType: 'new' | 'existing'
  name: string
  dealershipId?: string
  street: string
  city: string
  state: string
  zipCode: string
  phone: string
  email: string
  // Existing CRM Dealer
  crmDealerId?: string
  // Configuration Copy
  copyConfiguration: boolean
  sourceDealershipId?: string
}

// CRM dealers and source dealerships - populate from API when available
const crmDealers: { value: string; label: string }[] = []
const existingDealershipsForCopy: { value: string; label: string }[] = []

export function AddDealership() {
  const { selectedEntity, refreshData } = useDealership()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<AddDealershipFormValues>({
    initialValues: {
      creationType: 'new',
      name: '',
      dealershipId: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      email: '',
      copyConfiguration: false,
    },
    validate: {
      name: (value, values) => (values.creationType === 'new' && !value ? 'Dealership name is required' : null),
      dealershipId: (value, values) => {
        if (values.creationType === 'new') {
          if (!value || !value.trim()) {
            return 'Dealership ID is required'
          }
          if (!/^\d{1,6}$/.test(value.trim())) {
            return 'Dealership ID must be 1-6 digits'
          }
        }
        return null
      },
      crmDealerId: (value, values) => (values.creationType === 'existing' && !value ? 'Please select a CRM dealer' : null),
      street: (value, values) => (values.creationType === 'new' && !value ? 'Street address is required' : null),
      city: (value, values) => (values.creationType === 'new' && !value ? 'City is required' : null),
      state: (value, values) => (values.creationType === 'new' && !value ? 'State is required' : null),
      zipCode: (value, values) => (values.creationType === 'new' && !value ? 'ZIP code is required' : null),
    },
  })

  if (!selectedEntity || selectedEntity.type !== 'group') {
    return <Text>Please select a dealership group</Text>
  }

  const group = selectedEntity as DealershipGroup

  const handleSubmit = async (values: AddDealershipFormValues) => {
    setIsSubmitting(true)

    try {
      let dealershipId: string
      let configurationData: any

      if (values.creationType === 'new') {
        // Use provided dealershipId (required)
        dealershipId = values.dealershipId?.trim() || ''

        // Build configuration data from form values
        // Convert empty strings to undefined for optional fields
        configurationData = {
          storeName: values.name,
          address: {
            line1: values.street,
            line2: '',
            city: values.city,
            state: values.state,
            zipCode: values.zipCode,
          },
          primaryPhone: values.phone?.trim() || undefined,
          email: values.email?.trim() || undefined,
          website: undefined, // Not collected in form, can be added later
          timezone: 'America/Chicago',
        }
      } else {
        // For existing CRM dealer, use the CRM dealer ID
        // Note: This would need to be fetched from CRM API in a real implementation
        dealershipId = values.crmDealerId || ''
        configurationData = {
          storeName: values.name || 'Dealership',
          timezone: 'America/Chicago',
        }
      }

      await createDealership({
        dealershipId,
        groupId: group.id,
        configurationData,
      })

      notifications.show({
        title: 'Dealership Added',
        message: `${values.creationType === 'new' ? values.name : 'Dealership'} has been successfully added to ${group.name}`,
        color: 'green',
        icon: <IconCheck size={18} />,
      })

      // Refresh the dealership groups list to include the newly created dealership
      await refreshData()

      // Navigate to group dealers page
      navigate('/group/dealers')
    } catch (error: any) {
      // Extract error message from API response
      let errorMessage = 'Failed to add dealership. Please try again.'

      if (error.message) {
        errorMessage = error.message
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      }

      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box p="xl">
      <Stack gap="md" style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <Group>
          <Button variant="subtle" leftSection={<IconArrowLeft size={18} />} onClick={() => navigate('/group/dealers')}>
            Back to Dealers
          </Button>
        </Group>

        <Box>
          <Group gap="xs" align="center" mb={4}>
            <IconBuilding size={22} stroke={1.5} />
            <Title order={2}>Add Dealership</Title>
          </Group>
          <Text size="sm" c="dimmed">
            Add a new dealership to {group.name}
          </Text>
        </Box>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="xl">
            {/* Section 1: Business Basics */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Box>
                  <Text size="lg" fw={600} mb="xs">
                    1. Business Basics
                  </Text>
                  <Text size="sm" c="dimmed">
                    Enter basic dealership information or link to an existing CRM dealer
                  </Text>
                </Box>

                <Divider />

                {/* Creation Type */}
                <Radio.Group label="How would you like to add this dealership?" {...form.getInputProps('creationType')}>
                  <Stack gap="sm" mt="xs">
                    <Radio value="new" label="Create new dealership with manual entry" />
                    <Radio value="existing" label="Link existing dealer from CRM" />
                  </Stack>
                </Radio.Group>

                {/* New Dealership Form */}
                {form.values.creationType === 'new' && (
                  <Stack gap="md" mt="md">
                    <Group grow>
                      <TextInput
                        label="Dealership ID"
                        placeholder="1-6 digits (e.g., 4675)"
                        required
                        maxLength={6}
                        value={form.values.dealershipId || ''}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                          form.setFieldValue('dealershipId', value)
                        }}
                        onBlur={() => form.validateField('dealershipId')}
                        error={form.errors.dealershipId}
                      />
                      <TextInput
                        label="Dealership Name"
                        placeholder="e.g., Austin Toyota"
                        required
                        leftSection={<IconBuilding size={16} />}
                        {...form.getInputProps('name')}
                      />
                    </Group>

                    <TextInput
                      label="Street Address"
                      placeholder="123 Main Street"
                      required
                      leftSection={<IconMapPin size={16} />}
                      {...form.getInputProps('street')}
                    />

                    <Group grow>
                      <TextInput label="City" placeholder="Austin" required {...form.getInputProps('city')} />
                      <TextInput label="State" placeholder="TX" required {...form.getInputProps('state')} />
                      <TextInput label="ZIP Code" placeholder="78701" required {...form.getInputProps('zipCode')} />
                    </Group>

                    <Group grow>
                      <TextInput
                        label="Phone"
                        placeholder="(512) 555-0123"
                        leftSection={<IconPhone size={16} />}
                        maxLength={14}
                        value={form.values.phone || ''}
                        onChange={e => {
                          const formatted = formatPhoneNumber(e.target.value)
                          form.setFieldValue('phone', formatted)
                        }}
                        onBlur={() => form.validateField('phone')}
                        error={form.errors.phone}
                      />
                      <TextInput
                        label="Email"
                        placeholder="contact@dealer.com"
                        type="email"
                        leftSection={<IconMail size={16} />}
                        {...form.getInputProps('email')}
                      />
                    </Group>
                  </Stack>
                )}

                {/* Existing CRM Dealer Selection */}
                {form.values.creationType === 'existing' && (
                  <Stack gap="md" mt="md">
                    <Select
                      label="Select CRM Dealer"
                      placeholder={crmDealers.length === 0 ? 'No CRM dealers available' : 'Search for a dealer...'}
                      data={crmDealers}
                      searchable
                      required
                      leftSection={<IconBuilding size={16} />}
                      {...form.getInputProps('crmDealerId')}
                    />
                    <Paper p="md" withBorder style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                      <Text size="sm" c="dimmed">
                        <strong>Note:</strong> Linking an existing CRM dealer will automatically import dealership ID and name.
                        Additional details can be configured later.
                      </Text>
                    </Paper>
                  </Stack>
                )}
              </Stack>
            </Card>

            {/* Section 2: Configuration (Optional) */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Box>
                  <Group justify="space-between">
                    <div>
                      <Text size="lg" fw={600} mb="xs">
                        2. Configuration Setup
                      </Text>
                      <Text size="sm" c="dimmed">
                        Optionally copy settings from an existing dealership
                      </Text>
                    </div>
                    <Text
                      size="xs"
                      px="sm"
                      py={4}
                      style={{
                        backgroundColor: 'var(--mantine-color-gray-1)',
                        borderRadius: '4px',
                      }}
                    >
                      Optional
                    </Text>
                  </Group>
                </Box>

                <Divider />

                <Radio.Group
                  value={form.values.copyConfiguration ? 'copy' : 'skip'}
                  onChange={value => form.setFieldValue('copyConfiguration', value === 'copy')}
                >
                  <Stack gap="sm">
                    <Radio value="skip" label="Skip for now - I'll configure settings later" />
                    <Radio
                      value="copy"
                      label="Copy configuration from existing dealership"
                      description="Saves time by copying hours, integrations, and AI settings"
                    />
                  </Stack>
                </Radio.Group>

                {form.values.copyConfiguration && (
                  <Stack gap="md" mt="md">
                    <Select
                      label="Source Dealership"
                      placeholder={existingDealershipsForCopy.length === 0 ? 'No dealerships available to copy from' : 'Select a dealership to copy from...'}
                      data={existingDealershipsForCopy}
                      searchable
                      leftSection={<IconCopy size={16} />}
                      {...form.getInputProps('sourceDealershipId')}
                    />
                    <Paper p="md" withBorder style={{ backgroundColor: 'var(--mantine-color-teal-0)' }}>
                      <Stack gap="xs">
                        <Text size="sm" fw={600}>
                          The following will be copied:
                        </Text>
                        <Text size="sm" c="dimmed">
                          • Business hours and timezone
                          <br />
                          • CRM integration settings
                          <br />
                          • AI agent configuration
                          <br />
                          • Communication preferences
                          <br />• Workflow templates
                        </Text>
                        <Text size="xs" c="dimmed" mt="xs">
                          You can modify any of these settings after creation
                        </Text>
                      </Stack>
                    </Paper>
                  </Stack>
                )}

                {!form.values.copyConfiguration && (
                  <Paper p="md" withBorder style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                    <Text size="sm" c="dimmed">
                      You can configure business hours, integrations, and AI settings from the dealership configuration page after
                      creation.
                    </Text>
                  </Paper>
                )}
              </Stack>
            </Card>

            {/* Actions */}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => navigate('/group/dealers')} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting} leftSection={<IconCheck size={18} />}>
                Add Dealership
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Box>
  )
}
